import { toast } from 'react-toastify'
import {
  ProductCodes,
  CountryCodes,
  Regions,
  RegionsCurrencies,
  ContinentCodes,
} from '../constants'
import { ORGANIZATION_ROLES } from '../constants/Roles'

export const getRgbColor = (color) => {
  if (typeof color === 'string' && color.indexOf('#') > -1) {
    return color
  }

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`
}

export const triggerGoogleAnalyticsEvent = (eventName, metaData) => {
  window.gtag('event', eventName, metaData)
}

export const getNameIntials = (name) => {
  let initials = ''
  const [firstName, lastName] = name.split(' ')
  if (firstName) {
    initials += firstName[0].toUpperCase()
  }
  if (lastName) {
    initials += lastName[0].toUpperCase()
  }
  return initials
}

export const roundDown = (value) => {
  return Math.floor(value * 10) / 10
}

export const getAssetInMB = (sizeByte) => {
  return roundDown(sizeByte / 1000000)
}

export const getLocation = async () => {
  const location = await fetch('https://ipapi.co/json/').then((r) => r.json())
  return location
}

export const getUserCurrency = (location) => {
  const countryCode = location?.country_code?.toLowerCase()
  const continentCode = location?.continent_code?.toLowerCase()
  if (countryCode?.includes(CountryCodes.GB)) {
    return RegionsCurrencies[Regions.UK]
  } else if (continentCode?.includes(ContinentCodes.Europe)) {
    return RegionsCurrencies[Regions.Europe]
  }
  return RegionsCurrencies[Regions.Common]
}

export const sortAgreements = (agreements) => {
  return agreements.sort((a, b) => {
    const numA = parseInt(a.code.split('_')[2])
    const numB = parseInt(b.code.split('_')[2])
    return numA - numB
  })
}

export const copyTextToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      alert('Copied to clipboard!')
    })
    .catch((err) => {
      console.error('Failed to copy: ', err)
      throw err
    })
}

export const generateRandomString = (length) => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length)
    result += characters[randomIndex]
  }
  return result
}

export const getMonthlyRateCurrPrice = (currAmount) => {
  const currency = currAmount[0]
  const actualAmount = currAmount.substring(1)

  return `${currency}${(actualAmount / 12).toFixed(1)}`
}

export const getTotalSubscriptionPlusMembers = (
  singleAmount,
  memberCount = 1,
) => {
  memberCount = Number(memberCount) < 1 ? 1 : Number(memberCount)
  const currency = singleAmount[0]
  const actualAmount = Number(singleAmount.substring(1))

  return `${currency}${(actualAmount * memberCount).toFixed(1)}`
}

export const getUserOwnedActiveOrganization = (user) => {
  const ownerOrganization = user?.organizations?.find(
    (organization) =>
      organization.role === ORGANIZATION_ROLES.Owner &&
      organization.isCurrent &&
      organization.isActive,
  )

  return ownerOrganization
}

export const isOrganizationPlan = (plan) => {
  return [
    ProductCodes.Business,
    ProductCodes.BusinessPro,
    ProductCodes.Enterprise,
  ].includes(plan.code)
}

export const upgradeableOrgPlan = (plan) => {
  return [ProductCodes.Business, ProductCodes.BusinessPro].includes(plan.code)
}

export const subscriptionManager = ({
  authUser,
  userSubscription,
  products,
}) => {
  const isTrialPlan = userSubscription?.isTrialActive
  const subscriptionProduct = products.find(
    (product) => product._id === userSubscription.productId,
  )

  // check owner,because only owner can upgrade
  const organizationOwner = getUserOwnedActiveOrganization(authUser)

  // Check if the user is on a trial plan or free plan; Then they can upgrade
  let isUgradeablePlan = isTrialPlan || !userSubscription?.active

  const isOrgPlan =
    subscriptionProduct && isOrganizationPlan(subscriptionProduct)

  // If organization plan is upgradable, set to true
  if (isOrgPlan && upgradeableOrgPlan(subscriptionProduct)) {
    isUgradeablePlan = true
  }

  return {
    isUgradeablePlan: isOrgPlan
      ? isUgradeablePlan && !!organizationOwner // If org plan, the owner should be only user to upgrade such plan
      : isUgradeablePlan,
    isTrialPlan,
    isOrgPlan,
    subscriptionProduct,
    activeSubscription: userSubscription?.active,
  }
}

export const canUserSubscribeToProduct = (
  subscribedProduct,
  product,
  isTrialActive,
  isSubActive,
) => {
  let canSubscribe
  // const ownerOrganization = getUserOwnedActiveOrganization(user)
  switch (subscribedProduct?.code) {
    case ProductCodes.Free:
      canSubscribe = product.code === ProductCodes.SoloCreator
      break
    case ProductCodes.SoloCreator:
      canSubscribe =
        product?.code === ProductCodes.SoloCreator &&
        (isTrialActive || !isSubActive)
          ? true
          : false
      break
    case ProductCodes.Business:
      canSubscribe = [
        ProductCodes.BusinessPro,
        ProductCodes.Enterprise,
        ...(product?.code === ProductCodes.Business &&
        (isTrialActive || !isSubActive)
          ? [ProductCodes.Business]
          : []),
      ].includes(product.code)
      break
    case ProductCodes.BusinessPro:
      canSubscribe = [
        ProductCodes.Enterprise,
        ...(product?.code &&
        isOrganizationPlan(product) &&
        (isTrialActive || !isSubActive)
          ? [ProductCodes.BusinessPro, ProductCodes.Business]
          : []),
      ].includes(product.code)
      break
    case ProductCodes.Enterprise:
      canSubscribe =
        product?.code === ProductCodes.Enterprise &&
        (isTrialActive || !isSubActive)
      break
    default: {
      canSubscribe = true
    }
  }
  return canSubscribe
}

export const alertSubscriptionMessage = (message, autoClose = false) => {
  return toast.warn(
    <p>
      {message}
      <br />
      <a
        target="_blank"
        href="/user/subscribe"
        style={{ textDecoration: 'underline' }}>
        Access tools now
      </a>
    </p>,
    { autoClose },
  )
}

export const trimText = (text, len = 30) => {
  if (text.length <= len) {
    return text
  }
  return text.substring(0, len) + '...'
}

export const bookEnterpriseMeeting = () => {
  
  if (typeof window.lintrk === 'function') {
  window.lintrk('track', {
    conversion_id: 29178538
  });
}

window.open(
  'https://calendly.com/nageela-tikbox/30min',
  '_blank',
  'noopener,noreferrer'
);
  

  
  // window.open(
    
  //   'https://calendly.com/nageela-tikbox/30min',
  //   '_blank',
  //   'noopener,noreferrer',

    
//   )
}
